import Moment from 'moment';
import {
    osName,
    osVersion,
    deviceType,
    engineName,
    browserName,
    engineVersion,
} from 'react-device-detect';

import Crypto from '../security/Crypto';
import { assetRoutes } from '../routes/assets';
import { standardRoutes } from '../routes/standard';
import { APPLICATION, COOKIE_KEYS } from './Constants';
import CookieServices from '../services/CookieServices';
import StorageServices from '../services/StorageServices';

export function getAppTheme() {
    return APPLICATION.THEME
}

/**
 * Retrieves and validates a page size from browser cookies.
 * Ensures the value is a functional, positive integer for pagination.
 * 
 * @param {number} fallback - The default value if the cookie is missing or invalid.
 * @returns {number} - The validated page size.
 */
export const getPersistedPageSize0 = (fallback: number = 25): number => {
    const cookieValue = readFromCookie(COOKIE_KEYS.PAGE_COUNT);
    const parsed = Number(cookieValue);

    // Logic: Must be a finite number, greater than zero.
    // Fallback if the cookie is corrupted, missing, or value is "0".
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Synchronizes the page size between the application state and browser storage.
 * Logic:
 * 1. If 'newValue' is provided, it updates the cookie and returns that value.
 * 2. If 'newValue' is null, it attempts to read the existing cookie.
 * 3. If the cookie is missing/invalid, it sets the 'fallback' and returns it.
 */
/**
 * Synchronizes and validates the page size with persistence.
 * Performance Optimized: Only writes to cookies when a state change is detected.
 */
export const persistedPageSize = (
    pageSize: number | null = null,
): number => {
    const fallback: number = 25

    // Read the current value once to compare
    const storedValue = readFromCookie(COOKIE_KEYS.PAGE_COUNT);
    const parsedStored = Number(storedValue);

    const currentStoredValue = (Number.isFinite(parsedStored) && parsedStored > 0)
        ? parsedStored
        : null;

    // Determine the "Target" value
    // If a pageSize was passed, use it. Otherwise, use the stored value or fallback.
    const targetPageSize = (pageSize !== null && pageSize > 0)
        ? pageSize
        : (currentStoredValue ?? fallback);

    // 3. SMART SYNC: Only write if the cookie is missing or different
    if (targetPageSize !== currentStoredValue) {
        writeToCookie(COOKIE_KEYS.PAGE_COUNT, targetPageSize.toString());
    }

    return targetPageSize;
};



export function RouteHydration(apiRoute: string, findT: string, replaceT: any) {
    return apiRoute.replace(findT, replaceT)
}

export function DateFormating(dateString: any, format: string = 'ddd Do MMM YYYY') {
    return Moment(dateString).format(format)
}

export function DeviceInfo() {
    const osPart = `${osName} ${osVersion}`;
    const browserPart = `${browserName}/${engineName}`;

    return deviceType + ': ' + osPart + ' ' + browserPart + ' ' + engineVersion
}

export function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

export function titleCase(str: string) {
    return str.toLowerCase().split(' ').map((word) => {
        return word[0].toUpperCase() + word.slice(1)
    }).join(' ')
}

export function writeToCookie(key: string, data: any) {
    CookieServices.set(key, data, COOKIE_KEYS.OPTIONS)
}

export function readFromCookie(key: string) {
    return CookieServices.get(key)
}

export function encryptAndStoreCookie(key: string, data: any) {
    const encryptedData = Crypto.encryptDataUsingAES256(data)
    CookieServices.set(key, encryptedData, COOKIE_KEYS.OPTIONS)
}

export function encryptAndStoreLS(key: string, data: any) {
    // Local Storage data encryption and storage
    const strData = JSON.stringify(data);
    const encryptedData = Crypto.encryptDataUsingAES256(strData);

    StorageServices.setLocalStorage(key, JSON.stringify(encryptedData));
};

export function readDecryptAndParseLS(key: string) {
    const encryptedKeyString = StorageServices.getLocalStorage(key)
    const storageObject = JSON.parse(encryptedKeyString)

    const deStorageObject = Crypto.decryptDataUsingAES256(storageObject)
    return JSON.parse(deStorageObject)
}

export function getRandomObjectFromArray(array: any[]) {
    const randomIndex = Math.floor(Math.random() * array.length);
    return array[randomIndex];
}

export function displayNameColoring(letter: string) {
    const colorGroups = [
        ['A', 'F', 'K', 'P', 'U'],
        ['B', 'G', 'L', 'Q', 'V'],
        ['C', 'H', 'M', 'R', 'W'],
        ['D', 'I', 'N', 'S', 'X'],
        ['E', 'J', 'O', 'T', 'Y', 'Z']
    ];

    const colors = [
        'bg-emerald-600',
        'bg-cyan-600',
        'bg-indigo-600',
        'bg-orange-500',
        'bg-fuchsia-600'
    ];

    const groupIndex = colorGroups.findIndex(group => group.includes(letter.toUpperCase()));

    // Default to gray if not starting with a letter
    return groupIndex !== -1 ? colors[groupIndex] : 'bg-gray-500';
}

export function generateUniqueKey(length: number) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    const charactersLength = characters.length;

    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return result;
}

export function getStandardRoute(findByName: string) {
    return (standardRoutes.find(
        (routeName) => routeName.name === findByName)
    )?.path
}

export function getAssetRoute(findByName: string) {
    return (assetRoutes.find(
        (routeName) => routeName.name === findByName)
    )?.path
}

export function emailValidator(stringValue: string) {
    let lastAtPos = stringValue.lastIndexOf('@')
    let lastDotPos = stringValue.lastIndexOf('.')

    if (!(lastAtPos < lastDotPos && lastAtPos > 0 && stringValue.indexOf('@@') === -1 && lastDotPos > 2 && (stringValue.length - lastDotPos) > 2)) {
        return false
    }

    return true
}

export function passwordValidator(password: string) {
    const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#()\-[\]{}_\-+='\\|;:'",.<>\/~])[A-Za-z\d@$!%*?&#()\-[\]{}_\-+='\\|;:'",.<>\/~]{8,}$/;
    return strongPasswordRegex.test(password);
}

export function nameValidator(stringValue: string) {
    if (stringValue.match(new RegExp('[`!@#$%^&*()_+\-=\[\]{};:"\\|,.<>\/?~]'))) {
        return false
    } else {
        /* 
         * Remove allowed special characters then test for numbers
        */
        stringValue = stringValue.replace("'", '')
        stringValue = stringValue.replace(" ", '')

        if (!stringValue.match(new RegExp('[A-Z]')) || !stringValue.match(new RegExp('[a-z]'))) {
            return false
        }
    }

    return true
}

export function generateUniqueId(header: string) {
    let hash = 0;

    for (let i = 0; i < header.length; i++) {
        // Bitwise shifting creates the hash based on the characters
        hash = (hash << 5) - hash + header.charCodeAt(i);
        hash |= 0; // Convert to 32-bit integer
    }

    // Convert to a positive string and pad it to ensure it's long enough
    const sHash = Math.abs(hash).toString().padEnd(11, '0');

    // Slice it into the 0000-0000-000 format
    // Result example: "3829-1029-482"
    return `${sHash.slice(0, 4)}-${sHash.slice(4, 8)}-${sHash.slice(8, 11)}`;
}

export function parseCompactUuid(compact: string): string {
    return compact
        .toLowerCase()
        .replace(
            /^(.{8})(.{4})(.{4})(.{4})(.{12})$/,
            "$1-$2-$3-$4-$5"
        );
}

